/**
 * Authenticated Layout Route
 * Protects all child routes, requiring authentication
 *
 * Auth protection is handled in the component (not beforeLoad) because:
 * 1. beforeLoad cannot wait for async auth state to resolve
 * 2. This matches the pattern used in the Admin app
 * 3. Allows proper loading states while auth is being checked
 */
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { AppLayout } from "@/components/layout/AppLayout";
import { useAuth } from "@/hooks/useAuth";
import { signOut } from "@/lib/auth";
import { useEffect, useRef } from "react";
import { Loader2 } from "lucide-react";

export const Route = createFileRoute("/_authenticated")({
  component: AuthenticatedLayout,
});

function AuthenticatedLayout() {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const { user, isLoading, isAuthenticated, notAuthorizedForApp, authorizationErrorMessage } = useAuth();

  // Track whether auth has resolved successfully at least once.
  // After initial load, we keep AppLayout mounted during brief loading
  // flickers (e.g., session refetch on navigation) to prevent the sidebar
  // from unmounting and causing a visual "full page reload" effect.
  const hasLoadedOnce = useRef(false);
  if (!isLoading && isAuthenticated) {
    hasLoadedOnce.current = true;
  }

  // Redirect to login if not authenticated (after loading completes)
  useEffect(() => {
    if (!isLoading && !isAuthenticated && !notAuthorizedForApp) {
      navigate({
        to: "/login",
        search: { redirect: location.pathname },
      });
    }
  }, [isLoading, isAuthenticated, notAuthorizedForApp, navigate]);

  // Show loading spinner only on initial auth check (before first successful load).
  // Once auth has loaded once, skip the spinner to keep the sidebar stable.
  if (isLoading && !hasLoadedOnce.current) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <span className="text-sm text-muted-foreground">{t("auth.loading")}</span>
        </div>
      </div>
    );
  }

  // SECURITY: Block users who are not authorized for this app
  // This catches admin portal users trying to access the internal app
  if (notAuthorizedForApp) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-6 text-center">
        <div className="max-w-md">
          <h1 className="text-2xl font-semibold">{t("auth.noAccess")}</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {authorizationErrorMessage ?? t("auth.noAccessMessage")}
          </p>
          <a
            href="/login"
            className="mt-4 inline-block text-sm text-primary hover:underline"
            onClick={async (e) => {
              e.preventDefault();
              // Sign out Better Auth session, then redirect to login
              try { await signOut(); } catch { /* ignore */ }
              localStorage.clear();
              sessionStorage.clear();
              window.location.href = "/login";
            }}
          >
            {t("auth.switchAccount")}
          </a>
        </div>
      </div>
    );
  }

  // Don't render anything if not authenticated (will redirect via useEffect).
  // But if we've loaded once, keep AppLayout mounted during brief auth flickers.
  if (!isAuthenticated && !hasLoadedOnce.current) {
    return null;
  }

  // Block admin users from internal app (fallback for role-based check)
  if (user && ["administrator", "super_admin"].includes(user.role)) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-6 text-center">
        <div className="max-w-md">
          <h1 className="text-2xl font-semibold">{t("auth.noAccess")}</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {t("auth.adminOnly")}
          </p>
        </div>
      </div>
    );
  }

  return <AppLayout />;
}
